import { useOpenSettingTab } from '@affine/core/modules/dialogs/setting-navigation';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { useI18n } from '@affine/i18n';
import { LockIcon } from '@blocksuite/icons/rc';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { FeatureUpgradeNudge } from './feature-upgrade-nudge';
import * as styles from './plan-gated-surface.css';
import { useCanOpenWorkspacePlans } from './use-can-open-workspace-plans';

export type PlanGateState =
  | { status: 'loading' }
  | { status: 'allowed' }
  | { status: 'locked'; requiredPlan: string | null };

/**
 * Resolves a top-level surface against the workspace plan. Every key is
 * required; the first missing key decides which plan the hint names. The
 * state is one object as long as it says the same: callers depend on it.
 */
export function usePlanGate(featureKeys: readonly string[]): PlanGateState {
  const { isFeatureAllowed, getRequiredPlan, isLoading } = useFeatureGate();
  const missingFeature = isLoading
    ? undefined
    : featureKeys.find(key => !isFeatureAllowed(key));
  const requiredPlan =
    missingFeature === undefined ? null : getRequiredPlan(missingFeature);
  return useMemo<PlanGateState>(() => {
    if (isLoading) return { status: 'loading' };
    if (missingFeature === undefined) return { status: 'allowed' };
    return { status: 'locked', requiredPlan };
  }, [isLoading, missingFeature, requiredPlan]);
}

export interface PlanLockedSurfaceProps {
  /** Feature title shown on the overlay card (caller resolves via i18n). */
  featureName: string;
  requiredPlan: string | null;
  /**
   * Read-only preview of the page body (its real UI without user data). It is
   * rendered inert and must not mount data hooks or send requests: the server
   * rejects those calls for this plan.
   */
  children: ReactNode;
  testId: string;
}

/**
 * Canonical lock for plan-gated content (Mail, Workflows, Hub, Apps). The page
 * keeps its own header and sidebar entry; the body shows a read-only preview,
 * dimmed and lit from the top, with the plan card below the center. Clicking the
 * dimmed background moves the card away and leaves only a lock button that
 * brings it back. The preview stays inert throughout, so nothing in it can be
 * clicked or focused. The Plans CTA is for owners and admins only; other
 * members see the card without an action. Server-side feature guards stay
 * authoritative.
 */
export function PlanLockedSurface({
  featureName,
  requiredPlan,
  children,
  testId,
}: PlanLockedSurfaceProps): ReactElement {
  const t = useI18n();
  const canOpenPlans = useCanOpenWorkspacePlans();
  const openSettingTab = useOpenSettingTab();
  const [revealed, setRevealed] = useState(false);
  const openPlans = useCallback(
    () => openSettingTab('workspace:plans'),
    [openSettingTab]
  );

  return (
    <div className={styles.root} data-testid={testId} data-revealed={revealed}>
      <div className={styles.preview} inert aria-hidden="true">
        {children}
      </div>
      <div
        className={styles.scrim}
        data-testid={`${testId}-scrim`}
        aria-hidden="true"
        onClick={() => setRevealed(true)}
      />
      <div className={styles.dock}>
        <FeatureUpgradeNudge
          className={styles.card}
          testId={`${testId}-card`}
          featureName={featureName}
          requiredPlan={requiredPlan}
          ctaText={t['com.streamlain.ai-gate.view-plans']()}
          onUpgrade={canOpenPlans ? openPlans : null}
        />
      </div>
      <button
        type="button"
        className={styles.lockButton}
        data-testid={`${testId}-lock`}
        aria-label={featureName}
        title={featureName}
        onClick={() => setRevealed(false)}
      >
        <LockIcon />
      </button>
    </div>
  );
}
